/**
 * Tool-family taxonomy for the inline-prose tool row.
 *
 * The row treats tool calls as inline prose continuations of the
 * assistant's narration ("Edited foo.tsx", "Read build.py",
 * "Searched for `useEffect`") — same column, same font, with a
 * leading glyph and the family verb in bold. Every tool name the
 * SDK can emit maps to a small "family" record that carries:
 *
 *   * a coarse :class:`ToolFamilyId` (used by expand-body chrome
 *     when it cares about category-level grouping),
 *   * the bold leading **verb** the row reads with,
 *   * the leading **icon** that ledgers the row's tool kind so a
 *     long sequence of mixed calls is scannable at a glance.
 *
 * The icon is intentionally per-tool (not per ``ToolFamilyId``):
 * Bash vs Monitor, Read vs Write vs Edit each get their own glyph
 * because that's the granularity users triage at. Color is left
 * to ``currentColor`` so the icon picks up the same row tint as
 * the verb (neutral by default, red when errored, dimmer when
 * interrupted) without needing per-family palette logic.
 *
 * Families intentionally collapse multiple tools onto one visual:
 * ``Read`` / ``Write`` / ``Edit`` / ``MultiEdit`` / ``NotebookEdit``
 * all live in the file-IO family. ``Bash`` / ``Monitor`` /
 * ``BashOutput`` / ``KillBash`` / ``KillShell`` / ``TaskStop`` are the exec
 * family. ``Grep`` / ``Glob`` / ``WebSearch`` / ``WebFetch`` are
 * the search family. ``Task`` / ``Agent`` / ``Skill`` /
 * ``SlashCommand`` / ``ToolSearch`` are the agent family.
 * ``TodoWrite`` / ``ExitPlanMode`` are the planning family.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Bot,
  ClipboardCheck,
  Download,
  FilePen,
  FilePlus2,
  FileSearch,
  FileText,
  Globe,
  ListChecks,
  Radio,
  ScrollText,
  Search,
  Sparkles,
  Square,
  Terminal,
  TerminalSquare,
  Wrench,
} from 'lucide-react';

export type ToolFamilyId = 'exec' | 'fileio' | 'search' | 'agent' | 'planning';

export interface ToolFamily {
  id: ToolFamilyId;
  /**
   * Narration verb the row leads with — capitalized so a row reads
   * like a sentence: ``Edited foo.tsx`` / ``Monitor prime stream``.
   * When the tool's own ``description`` starts with an imperative
   * verb the summary overrides this with that verb instead so the
   * row never doubles ("Ran Re-run …").
   */
  verb: string;
  /**
   * Leading glyph for the row. Per-tool granularity so foreground
   * shell, background shell, todo, read, write, etc. each carry
   * their own scannable mark. Color stays ``currentColor`` so the
   * icon tracks the row's tint (neutral / red / dim) without any
   * extra coordination.
   */
  icon: LucideIcon;
}

const FAMILY_BY_TOOL: Record<string, ToolFamily> = {
  Bash: { id: 'exec', verb: 'Ran', icon: Terminal },
  bash: { id: 'exec', verb: 'Ran', icon: Terminal },
  Monitor: { id: 'exec', verb: 'Monitor', icon: Radio },
  BashOutput: { id: 'exec', verb: 'Output of', icon: ScrollText },
  KillBash: { id: 'exec', verb: 'Killed', icon: Square },
  KillShell: { id: 'exec', verb: 'Killed', icon: Square },
  TaskStop: { id: 'exec', verb: 'Stopped', icon: Square },

  Read: { id: 'fileio', verb: 'Read', icon: FileText },
  ReadFile: { id: 'fileio', verb: 'Read', icon: FileText },
  Write: { id: 'fileio', verb: 'Wrote', icon: FilePlus2 },
  WriteFile: { id: 'fileio', verb: 'Wrote', icon: FilePlus2 },
  Edit: { id: 'fileio', verb: 'Edited', icon: FilePen },
  EditFile: { id: 'fileio', verb: 'Edited', icon: FilePen },
  MultiEdit: { id: 'fileio', verb: 'Edited', icon: FilePen },
  MultiEditFile: { id: 'fileio', verb: 'Edited', icon: FilePen },
  NotebookEdit: { id: 'fileio', verb: 'Edited', icon: FilePen },

  Grep: { id: 'search', verb: 'Searched for', icon: Search },
  Glob: { id: 'search', verb: 'Matched', icon: FileSearch },
  WebSearch: { id: 'search', verb: 'Searched the web for', icon: Globe },
  WebFetch: { id: 'search', verb: 'Fetched', icon: Download },

  Task: { id: 'agent', verb: 'Launched', icon: Bot },
  Agent: { id: 'agent', verb: 'Launched', icon: Bot },
  Skill: { id: 'agent', verb: 'Used skill', icon: Sparkles },
  SkillTool: { id: 'agent', verb: 'Used skill', icon: Sparkles },
  SlashCommand: { id: 'agent', verb: 'Ran command', icon: TerminalSquare },
  ToolSearch: { id: 'agent', verb: 'Searched tools for', icon: Search },

  TodoWrite: { id: 'planning', verb: 'Updated todos', icon: ListChecks },
  TaskCreate: { id: 'planning', verb: 'Created task', icon: ListChecks },
  TaskUpdate: { id: 'planning', verb: 'Updated task', icon: ListChecks },
  TaskGet: { id: 'planning', verb: 'Read task', icon: ListChecks },
  TaskList: { id: 'planning', verb: 'Listed', icon: ListChecks },
  ExitPlanMode: { id: 'planning', verb: 'Proposed plan', icon: ClipboardCheck },
};

/**
 * Look up the family record for a tool name. Unknown tools fall
 * back to a neutral ``Called`` row with a wrench glyph so they
 * still ledger cleanly without claiming one of the known
 * categories.
 */
export function toolFamily(name: string): ToolFamily {
  return FAMILY_BY_TOOL[name] ?? { id: 'planning', verb: 'Called', icon: Wrench };
}
